import { MiniSelect } from "@/components/dashboard/editable";
import { PageHeader } from "@/components/dashboard/page-header";
import { SectionLabel } from "@/components/dashboard/section-label";
import { Input } from "@/components/ui/input";
import { ConfirmDialog, Modal } from "@/components/ui/modal";
import { cn } from "@/lib/utils";
import { Trash2, UserPlus } from "lucide-react";
import { type FormEvent, useState } from "react";

// Admin-only management surface (the client never reaches it). Controls are always live: the access level is
// an inline select, Invite adds a pending seat, and removal is confirmed. Two access levels only — Admin
// (OW staff, full edit) and Viewer (client, read-only). Representative data; nothing here is persisted.

type Role = "admin" | "viewer";
type Status = "active" | "pending";

const ROLE_OPTIONS: { value: Role; label: string }[] = [
  { value: "admin", label: "Admin" },
  { value: "viewer", label: "Viewer" },
];
// Shown under the access select in the invite dialog so the picker explains itself.
const ROLE_DESC: Record<Role, string> = {
  admin: "Full edit access — manages roles, candidates, the pipeline, and the team.",
  viewer: "Reads the entire portal. Never sees internal notes or private interview rounds.",
};

type Person = {
  id: string;
  name: string;
  email: string;
  role: Role;
  status: Status;
  you?: boolean;
};
const INITIAL_PEOPLE: Person[] = [
  { id: "u1", name: "Alex Morgan", email: "alex.morgan@oliverwyman.com", role: "admin", status: "active", you: true },
  { id: "u2", name: "Priya Shah", email: "priya.shah@oliverwyman.com", role: "admin", status: "active" },
  { id: "u3", name: "Jordan Lee", email: "jordan.lee@client.com", role: "viewer", status: "active" },
  { id: "u4", name: "Sam Rivera", email: "sam.rivera@client.com", role: "viewer", status: "active" },
  { id: "u5", name: "Casey Tan", email: "casey.tan@client.com", role: "viewer", status: "active" },
  // A still-outstanding invitation — the "pending" seat state, so it always has a concrete example.
  { id: "u6", name: "", email: "morgan.reyes@client.com", role: "viewer", status: "pending" },
];

function initials(nameOrEmail: string) {
  const parts = nameOrEmail.split(/\s+/).filter(Boolean);
  const base = parts.length > 1 ? (parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "") : nameOrEmail.slice(0, 2);
  return base.toUpperCase() || "?";
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** A small amber pill for the pending state — colour is the signal (matches the pending count in the header),
 *  no dot needed. */
function PendingPill() {
  return (
    <span className="inline-flex items-center rounded-full bg-[color-mix(in_oklab,var(--color-warning)_14%,transparent)] px-2 py-0.5 text-[11px] font-medium text-[var(--color-warning)]">
      Pending
    </span>
  );
}

/** One member row. Identity on the left carries everything about the person — name, the Pending chip for
 *  an unaccepted invite, and their email. The right holds just the two controls that act on the row: the
 *  fixed-width access select (so every dropdown lines up) and remove. Access level is plain text in the
 *  select — Admin vs Viewer is self-explanatory, no colour key needed. */
function PersonRow({ p, onRole, onRemove }: { p: Person; onRole: (role: Role) => void; onRemove: () => void }) {
  const pending = p.status === "pending";
  const display = p.name || p.email;
  return (
    <div className="flex items-center gap-3.5 px-4 py-3">
      <span
        className={cn(
          "grid size-9 shrink-0 place-items-center rounded-full text-xs font-semibold",
          pending
            ? "border border-dashed border-border text-muted-foreground"
            : "bg-secondary text-secondary-foreground",
        )}
      >
        {initials(display)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-foreground">{display}</span>
          {p.you ? (
            <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
              You
            </span>
          ) : null}
          {pending ? <PendingPill /> : null}
        </div>
        {!pending ? <p className="truncate text-xs text-muted-foreground">{p.email}</p> : null}
      </div>

      {/* access — fixed width so every dropdown lines up */}
      <div className="w-32 shrink-0">
        <MiniSelect
          ariaLabel={`Access level for ${display}`}
          value={p.role}
          onChange={(v) => onRole(v as Role)}
          options={ROLE_OPTIONS}
          className="w-full"
        />
      </div>
      <button
        type="button"
        onClick={onRemove}
        aria-label={pending ? `Revoke invitation for ${display}` : `Remove ${display}`}
        className="shrink-0 text-muted-foreground/50 outline-none transition-colors hover:text-[var(--color-destructive)] focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Trash2 className="size-4" />
      </button>
    </div>
  );
}

/** The Invite flow — a small form dialog that adds a pending seat. Its own component so its draft state
 *  resets cleanly on close. */
function InviteDialog({
  open,
  onOpenChange,
  onInvite,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInvite: (email: string, role: Role) => void;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("viewer");
  const valid = EMAIL_RE.test(email.trim());

  const close = (o: boolean) => {
    if (!o) {
      setEmail("");
      setRole("viewer");
    }
    onOpenChange(o);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    onInvite(email.trim(), role);
    close(false);
  };

  return (
    <Modal
      open={open}
      onOpenChange={close}
      className="w-[440px]"
      title="Invite to the portal"
      description="They'll get an email with a link to join. You can change or revoke their access at any time."
    >
      <form onSubmit={submit} className="mt-4 space-y-4">
        <div className="space-y-1.5">
          <label htmlFor="invite-email" className="field-label">
            Email address
          </label>
          {/* Radix Dialog moves focus to the first field (this input) on open, so no explicit autoFocus. */}
          <Input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@company.com"
          />
        </div>
        <div className="space-y-1.5">
          <span className="field-label">Access level</span>
          <MiniSelect
            ariaLabel="Access level for the invitee"
            value={role}
            onChange={(v) => setRole(v as Role)}
            options={ROLE_OPTIONS}
            fullWidth
          />
          <p className="text-xs text-muted-foreground">{ROLE_DESC[role]}</p>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={() => close(false)}
            className="inline-flex h-9 items-center rounded-md border bg-card px-3.5 text-sm font-medium text-foreground outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!valid}
            className="inline-flex h-9 items-center rounded-md bg-foreground px-3.5 text-sm font-medium text-background outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
          >
            Send invitation
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** Team & access — who can open this portal, and what they're allowed to do. Its own destination in the
 *  admin nav. Everything is session-local in this mock (the caption says so). */
export function TeamAccessPage() {
  const [people, setPeople] = useState<Person[]>(INITIAL_PEOPLE);
  const [toRemove, setToRemove] = useState<Person | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const pendingCount = people.filter((p) => p.status === "pending").length;

  const setRole = (id: string, role: Role) => setPeople((list) => list.map((p) => (p.id === id ? { ...p, role } : p)));
  const remove = (id: string) => setPeople((list) => list.filter((p) => p.id !== id));
  const invite = (email: string, role: Role) =>
    setPeople((list) => [{ id: `invite-${email}-${list.length}`, name: "", email, role, status: "pending" }, ...list]);

  return (
    <div className="animate-fade-up space-y-6">
      <PageHeader title="Team & access" description="Who can open this portal, and what they're allowed to do." />

      <div>
        <SectionLabel
          meta={
            <>
              <span className="font-mono text-xs uppercase tracking-[0.04em] text-muted-foreground">
                <span className="font-medium tabular-nums text-foreground">{people.length}</span>{" "}
                {people.length === 1 ? "person" : "people"}
              </span>
              {pendingCount ? (
                <span className="inline-flex items-center rounded-full bg-[color-mix(in_oklab,var(--color-warning)_14%,transparent)] px-2 py-0.5 text-[11px] font-medium text-[var(--color-warning)]">
                  {pendingCount} pending
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => setInviteOpen(true)}
                className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md bg-foreground px-2.5 text-xs font-medium text-background outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring"
              >
                <UserPlus className="size-3.5" /> Invite
              </button>
            </>
          }
        >
          People
        </SectionLabel>

        {people.length === 0 ? (
          <div className="rounded-xl border border-dashed bg-card px-4 py-10 text-center text-sm text-muted-foreground">
            No one has access yet. Invite someone to get started.
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border bg-card">
            <div className="divide-y">
              {people.map((p) => (
                <PersonRow key={p.id} p={p} onRole={(role) => setRole(p.id, role)} onRemove={() => setToRemove(p)} />
              ))}
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={toRemove !== null}
        onOpenChange={(o) => {
          if (!o) setToRemove(null);
        }}
        title={
          toRemove?.status === "pending" ? "Revoke this invitation?" : `Remove ${toRemove?.name || toRemove?.email}?`
        }
        description={
          toRemove?.status === "pending"
            ? `${toRemove?.email} will no longer be able to join the portal with this invitation.`
            : `${toRemove?.name || toRemove?.email} will immediately lose access to this portal. You can invite them again at any time.`
        }
        confirmLabel={toRemove?.status === "pending" ? "Revoke invitation" : "Remove"}
        destructive
        onConfirm={() => {
          if (toRemove) remove(toRemove.id);
        }}
      />

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} onInvite={invite} />
    </div>
  );
}
